class MisbhvPairGallery {
  constructor(section) {
    this.section = section;
    this.gallery = section.querySelector("gallery-section");
    this.frame = this.gallery?.querySelector("[data-gallery]");
    this.track = this.frame?.querySelector("[data-swiper-container]");
    this.page = 0;
    this.touchStart = null;
    this.didSwipe = false;
    this.zoomOpener = null;

    if (!this.gallery || !this.frame || !this.track) return;
    this.initialize();
  }

  initialize() {
    if (this.gallery.dataset.pairGalleryReady === "true") return;

    this.gallery.dataset.pairGalleryReady = "true";
    this.gallery.classList.add("misbhv-pair-gallery");
    this.frame.setAttribute("tabindex", "0");
    this.frame.setAttribute("role", "region");
    this.frame.setAttribute("aria-roledescription", "carousel");
    this.frame.setAttribute("aria-label", "Product image gallery");

    this.controls = document.createElement("div");
    this.controls.className = "misbhv-pair-gallery__controls";
    this.controls.innerHTML = `
      <button class="misbhv-pair-gallery__edge misbhv-pair-gallery__edge--prev" type="button" aria-label="Previous image pair">
        <span aria-hidden="true">&lsaquo;</span>
      </button>
      <button class="misbhv-pair-gallery__edge misbhv-pair-gallery__edge--next" type="button" aria-label="Next image pair">
        <span aria-hidden="true">&rsaquo;</span>
      </button>
    `;
    this.frame.appendChild(this.controls);

    this.previousButton = this.controls.querySelector(".misbhv-pair-gallery__edge--prev");
    this.nextButton = this.controls.querySelector(".misbhv-pair-gallery__edge--next");

    this.previousButton.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.move(-1);
    });

    this.nextButton.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.move(1);
    });

    this.frame.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") this.move(-1);
      if (event.key === "ArrowRight") this.move(1);
    });

    this.frame.addEventListener("touchstart", (event) => {
      this.touchStart = event.changedTouches[0].clientX;
      this.didSwipe = false;
    }, { passive: true });

    this.frame.addEventListener("touchend", (event) => {
      if (this.touchStart === null) return;

      const distance = event.changedTouches[0].clientX - this.touchStart;
      if (Math.abs(distance) > 36) {
        this.didSwipe = true;
        this.move(distance > 0 ? -1 : 1);
      }
      this.touchStart = null;
    }, { passive: true });

    this.createZoom();
    this.observer = new MutationObserver(() => this.refresh());
    this.observer.observe(this.track, { childList: true });
    this.galleryBreakpoint = window.matchMedia("(max-width: 1099px)");
    this.handleBreakpointChange = () => this.refresh();
    if (this.galleryBreakpoint.addEventListener) {
      this.galleryBreakpoint.addEventListener("change", this.handleBreakpointChange);
    } else {
      this.galleryBreakpoint.addListener(this.handleBreakpointChange);
    }
    this.refresh();
  }

  get slides() {
    return Array.from(this.track.children).filter((slide) => slide.matches("[data-swiper-slide]"));
  }

  refresh() {
    const previousItemsPerPage = this.itemsPerPage || 2;
    const firstVisibleIndex = this.page * previousItemsPerPage;

    this.itemsPerPage = this.galleryBreakpoint?.matches ? 1 : 2;
    this.total = Math.max(1, Math.ceil(this.slides.length / this.itemsPerPage));
    this.page = Math.min(
      Math.floor(firstVisibleIndex / this.itemsPerPage),
      this.total - 1,
    );
    this.controls.hidden = this.slides.length <= this.itemsPerPage;
    const modeLabel = this.itemsPerPage === 1 ? "image" : "image pair";
    this.previousButton.setAttribute("aria-label", `Previous ${modeLabel}`);
    this.nextButton.setAttribute("aria-label", `Next ${modeLabel}`);
    this.render();
  }

  move(direction) {
    this.page = (this.page + direction + this.total) % this.total;
    this.render();
  }

  render() {
    this.track.style.setProperty("--misbhv-pair-page", this.page);
    this.slides.forEach((slide, index) => {
      const isVisible = Math.floor(index / this.itemsPerPage) === this.page;
      slide.setAttribute("aria-hidden", String(!isVisible));
    });
  }

  get zoomImages() {
    return this.slides.map(slide => slide.querySelector('img.wt-product__img')).filter(Boolean);
  }

  moveZoom(direction) {
    const images = this.zoomImages;
    if (images.length < 2) return;
    const current = Math.max(0, images.indexOf(this.zoomSource));
    this.openZoom(images[(current + direction + images.length) % images.length], true);
  }

  createZoom() {
    if (!document.getElementById('misbhv-zoom-navigation-style')) {
      const style = document.createElement('style');
      style.id = 'misbhv-zoom-navigation-style';
      style.textContent = `body .misbhv-pair-zoom__nav { position:absolute!important; top:50%!important; transform:translateY(-50%)!important; z-index:3!important; width:44px!important; height:52px!important; padding:0!important; border:0!important; background:#fff!important; color:#000!important; font:32px/1 Arial,sans-serif!important; cursor:pointer; } body .misbhv-pair-zoom__nav--prev {left:8px!important;} body .misbhv-pair-zoom__nav--next {right:8px!important;} body .misbhv-pair-zoom__nav[hidden] {display:none!important;} body .misbhv-pair-zoom__count {position:absolute;bottom:3px;left:50%;transform:translateX(-50%);z-index:3;background:#fff;color:#000;font:12px/1.5 Arial,sans-serif;padding:2px 8px;}`;
      document.head.append(style);
    }
    this.zoom = document.createElement("div");
    this.zoom.className = "misbhv-pair-zoom";
    this.zoom.hidden = true;
    this.zoom.setAttribute("role", "dialog");
    this.zoom.setAttribute("aria-modal", "true");
    this.zoom.setAttribute("aria-label", "Image zoom");
    this.zoom.innerHTML = `
      <button class="misbhv-pair-zoom__close" type="button" aria-label="Close image zoom">&times;</button>
      <button class="misbhv-pair-zoom__nav misbhv-pair-zoom__nav--prev" type="button" aria-label="Previous image">&#8249;</button>
      <button class="misbhv-pair-zoom__nav misbhv-pair-zoom__nav--next" type="button" aria-label="Next image">&#8250;</button>
      <span class="misbhv-pair-zoom__count" role="status" aria-live="polite"></span>
      <div class="misbhv-pair-zoom__stage">
        <img class="misbhv-pair-zoom__image" alt="" tabindex="0" role="button" aria-label="Toggle larger zoom">
      </div>
    `;
    document.body.appendChild(this.zoom);

    this.zoomStage = this.zoom.querySelector(".misbhv-pair-zoom__stage");
    this.zoomImage = this.zoom.querySelector(".misbhv-pair-zoom__image");
    this.zoomClose = this.zoom.querySelector(".misbhv-pair-zoom__close");

    this.track.addEventListener("click", (event) => {
      const sourceImage = event.target.closest(".wt-product__img");
      if (!sourceImage || this.didSwipe) {
        this.didSwipe = false;
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.openZoom(sourceImage);
    }, true);

    this.zoomPrevious = this.zoom.querySelector('.misbhv-pair-zoom__nav--prev');
    this.zoomNext = this.zoom.querySelector('.misbhv-pair-zoom__nav--next');
    this.zoomCount = this.zoom.querySelector('.misbhv-pair-zoom__count');
    this.zoomPrevious.addEventListener('click', () => this.moveZoom(-1));
    this.zoomNext.addEventListener('click', () => this.moveZoom(1));
    this.zoom.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        event.stopPropagation();
        this.moveZoom(event.key === 'ArrowLeft' ? -1 : 1);
      }
    });
    this.zoomClose.addEventListener("click", () => this.closeZoom());
    this.zoomStage.addEventListener("click", (event) => {
      if (event.target === this.zoomStage) this.closeZoom();
    });
    this.zoomImage.addEventListener("click", () => this.toggleZoom());
    this.zoomImage.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.toggleZoom();
      }
    });
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !this.zoom.hidden) this.closeZoom();
    });
  }

  openZoom(sourceImage, navigating = false) {
    const highResolutionLink = sourceImage.closest("a")?.href;
    if (!navigating) {
      this.zoomOpener = sourceImage.closest("a") || this.frame;
      this.previousBodyOverflow = document.body.style.overflow;
    }
    this.zoomSource = sourceImage;
    const images = this.zoomImages;
    this.zoomPrevious.hidden = this.zoomNext.hidden = images.length < 2;
    this.zoomCount.textContent = (images.indexOf(sourceImage) + 1) + ' / ' + images.length;
    const preview = sourceImage.currentSrc || sourceImage.src;
    const request = (this.zoomRequest || 0) + 1;
    this.zoomRequest = request;
    this.zoomImage.src = preview;
    this.zoomImage.loading = 'eager';
    // Keep the already-loaded preview if the full-size image fails.
    if (highResolutionLink && highResolutionLink !== preview) {
      const fullImage = new Image();
      fullImage.onload = () => {
        if (this.zoomRequest !== request || this.zoom.hidden) return;
        if (fullImage.naturalWidth > 0) this.zoomImage.src = fullImage.src;
      };
      fullImage.onerror = () => { /* The preview remains visible. */ };
      fullImage.src = highResolutionLink;
    }
    this.zoomImage.alt = sourceImage.alt;
    this.zoomImage.classList.remove("is-zoomed");
    this.zoom.hidden = false;
    this.zoomStage.scrollLeft = 0;
    this.zoomStage.scrollTop = 0;
    document.body.style.overflow = "hidden";
    if (!navigating) this.zoomClose.focus();
  }

  closeZoom() {
    this.zoomRequest = (this.zoomRequest || 0) + 1;
    this.zoom.hidden = true;
    this.zoomImage.classList.remove("is-zoomed");
    this.zoomImage.removeAttribute("src");
    document.body.style.overflow = this.previousBodyOverflow || "";
    this.zoomOpener?.focus();
  }

  toggleZoom() {
    const enlarged = this.zoomImage.classList.toggle("is-zoomed");
    this.zoomStage.scrollLeft = enlarged ? (this.zoomStage.scrollWidth - this.zoomStage.clientWidth) / 2 : 0;
    this.zoomStage.scrollTop = enlarged ? (this.zoomStage.scrollHeight - this.zoomStage.clientHeight) / 2 : 0;
  }
}

const initializeMisbhvPairGalleries = (root = document) => {
  const selector = '.misbhv-product-editorial[desktop-layout="fashion"]';
  const sections = root.matches?.(selector)
    ? [root]
    : Array.from(root.querySelectorAll?.(selector) || []);

  sections.forEach((section) => new MisbhvPairGallery(section));
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initializeMisbhvPairGalleries());
} else {
  initializeMisbhvPairGalleries();
}

document.addEventListener("shopify:section:load", (event) => {
  initializeMisbhvPairGalleries(event.target);
});
